import { useMedia } from './useMedia';

declare global {
  interface Navigator {
    msMaxTouchPoints?: number;
  }
}

/**
 * Checks if the current environment supports true touch input.
 *
 * @returns `true` if any touch points are reported or `ontouchstart` exists; otherwise `false`.
 *
 * @edge Stylus: Pen input may not increment touch points or fire `ontouchstart`.
 * @edge Desktops w/Touch: May return `true` even if screen touched rarely; combine with pointer query to reduce false positives.
 */
export const hasTrueTouch = (): boolean =>
  navigator.maxTouchPoints > 0 || (navigator.msMaxTouchPoints ?? 0) > 0 || 'ontouchstart' in window;

/**
 * Determines if the device is primarily touch-capable (e.g., smartphone/tablet).
 *
 * @returns `true` when both a coarse pointer and true touch support are detected; otherwise `false`.
 *
 * @edge StylusPhones: Still returns `true`, since finger input remains coarse.
 * @edge TouchLaptops: May return `false`, as pointer type may be `fine`.
 * @edge SSR/Env: Safely returns `false` if underlying APIs are unavailable.
 */
export function useIsTouchDevice(): boolean {
  const isCoarse = useMedia('(pointer: coarse)');
  return isCoarse && hasTrueTouch();
}
